import type { NodeSpec } from '@atlaskit/editor-prosemirror/model';
import { acNameToEmoji, acShortcutToEmoji } from '../../utils/confluence/emoji';
import type { AnnotationMarkDefinition } from '../marks/annotation';
import { emoji as emojiFactory } from '../../next-schema/generated/nodeTypes';
import { uuid } from '../../utils/uuid';

/**
 * @name emoji_node
 */
export interface EmojiDefinition {
	attrs: EmojiAttributes;
	/**
	 // eslint-disable-next-line eslint-plugin-jsdoc/check-tag-names
	 * @stage 0
	 */
	marks?: Array<AnnotationMarkDefinition>;
	type: 'emoji';
}

export interface EmojiAttributes {
	id?: string; // Optional to support legacy formats
	localId?: string;
	shortName: string;
	text?: string;
}

export const emoji: NodeSpec = emojiFactory({
	parseDOM: [
		// Handle copy/paste beautiful panel from renderer />
		{
			tag: 'div.ak-editor-panel__icon span',
			ignore: true,
		},
		{
			tag: 'span[data-emoji-short-name]',
			getAttrs: (domNode) => {
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				const dom = domNode as HTMLElement;
				return {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					shortName:
						dom.getAttribute('data-emoji-short-name') ||
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						emoji.attrs!.shortName.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					id: dom.getAttribute('data-emoji-id') || emoji.attrs!.id.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					text:
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						dom.getAttribute('data-emoji-text') || emoji.attrs!.text.default,
				};
			},
		},
		// Handle copy/paste from old <ac:emoticon />
		{
			tag: 'img[data-emoticon-name]',
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			getAttrs: (dom) =>
				acNameToEmoji(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(dom as Element).getAttribute('data-emoticon-name') as any,
				),
		},
		// Handle copy/paste from old <ac:hipchat-emoticons />
		{
			tag: 'img[data-hipchat-emoticon]',
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			getAttrs: (dom) =>
				acShortcutToEmoji(
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					(dom as Element).getAttribute('data-hipchat-emoticon')!,
				),
		},
		// Handle copy/paste from bitbucket's <img class="emoji" />
		{
			tag: 'img.emoji[data-emoji-short-name]',
			getAttrs: (domNode) => {
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				const dom = domNode as HTMLElement;
				return {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					shortName:
						dom.getAttribute('data-emoji-short-name') ||
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						emoji.attrs!.shortName.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					id: dom.getAttribute('data-emoji-id') || emoji.attrs!.id.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					text:
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						dom.getAttribute('data-emoji-text') || emoji.attrs!.text.default,
				};
			},
		},
	],
	toDOM(node) {
		const { shortName, id, text } = node.attrs;
		const attrs = {
			'data-emoji-short-name': shortName,
			'data-emoji-id': id,
			'data-emoji-text': text,
			contenteditable: 'false',
		};
		return ['span', attrs, text];
	},
});

export const emojiWithLocalId: NodeSpec = emojiFactory({
	parseDOM: [
		// Handle copy/paste beautiful panel from renderer />
		{
			tag: 'div.ak-editor-panel__icon span',
			ignore: true,
		},
		{
			tag: 'span[data-emoji-short-name]',
			getAttrs: (domNode) => {
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				const dom = domNode as HTMLElement;
				return {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					shortName:
						dom.getAttribute('data-emoji-short-name') ||
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						emoji.attrs!.shortName.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					id: dom.getAttribute('data-emoji-id') || emoji.attrs!.id.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					text:
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						dom.getAttribute('data-emoji-text') || emoji.attrs!.text.default,
					localId: uuid.generate(),
				};
			},
		},
		// Handle copy/paste from old <ac:emoticon />
		{
			tag: 'img[data-emoticon-name]',
			getAttrs: (dom) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const attrs = acNameToEmoji(
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(dom as Element).getAttribute('data-emoticon-name') as any,
				);
				return {
					...attrs,
					localId: uuid.generate(),
				};
			},
		},
		// Handle copy/paste from old <ac:hipchat-emoticons />
		{
			tag: 'img[data-hipchat-emoticon]',
			getAttrs: (dom) => {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const attrs = acShortcutToEmoji(
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					(dom as Element).getAttribute('data-hipchat-emoticon')!,
				);
				return {
					...attrs,
					localId: uuid.generate(),
				};
			},
		},
		// Handle copy/paste from bitbucket's <img class="emoji" />
		{
			tag: 'img.emoji[data-emoji-short-name]',
			getAttrs: (domNode) => {
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				const dom = domNode as HTMLElement;
				return {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					shortName:
						dom.getAttribute('data-emoji-short-name') ||
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						emoji.attrs!.shortName.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					id: dom.getAttribute('data-emoji-id') || emoji.attrs!.id.default,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					text:
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						dom.getAttribute('data-emoji-text') || emoji.attrs!.text.default,
					localId: uuid.generate(),
				};
			},
		},
	],
	toDOM(node) {
		const { shortName, id, text } = node.attrs;
		const attrs = {
			'data-emoji-short-name': shortName,
			'data-emoji-id': id,
			'data-emoji-text': text,
			contenteditable: 'false',
			'data-local-id': node?.attrs?.localId || undefined,
		};
		return ['span', attrs, text];
	},
});
